import React, { useEffect, useMemo, useState } from "react";
import { NepaliDatePicker } from "nepali-calander";
import Button from "../../../shared/Button";
import swal from "sweetalert";
import { Tooltip } from "@mui/material";

const AssessmentRow = ({
  hasTheory,
  hasPractical,
  handleChange,
  yearLevelId,
  classroomId,
  subjectId,
  subjectName,
  assessmentSubjectConfigurations,
  setAssessmentSubjectConfigurations,
  defaultStartTime,
  defaultHour,
  defaultMinutes,
  setSelectAll,
}) => {
  const [calendarMode, setCalendarMode] = useState(
    localStorage.getItem("calendar-mode"),
  );

  const assessmentConfig = useMemo(() => {
    return assessmentSubjectConfigurations?.find(
      (c) =>
        c.yearLevelId === yearLevelId &&
        c.classroomId === classroomId &&
        c.subjectId === subjectId,
    );
  }, [yearLevelId, classroomId, subjectId, assessmentSubjectConfigurations]);

  //clear row
  const clearRow = (yearLevelId, classroomId, subjectId) => {
    const newAssessmentSubjectConfigurations =
      assessmentSubjectConfigurations?.map((item) => {
        if (
          item?.yearLevelId !== yearLevelId ||
          item?.classroomId !== classroomId ||
          item?.subjectId !== subjectId
        )
          return item;

        return {
          ...item,
          checked: false,

          ...(item?.hasTheory && {
            assessmentTheoryFromDate: "",
            assessmentTheoryStartTime: "",
            assessmentTheoryHour: "",
            assessmentTheoryMinutes: "",
            recordingTheoryDate: "",
            recordingTheoryDeadlineDate: "",
          }),

          ...(item?.hasPractical && {
            assessmentPracticalFromDate: "",
            assessmentPracticalStartTime: "",
            assessmentPracticalHour: "",
            assessmentPracticalMinutes: "",
            recordingPracticalDate: "",
            recordingPracticalDeadlineDate: "",
          }),
        };
      });

    setAssessmentSubjectConfigurations(newAssessmentSubjectConfigurations);
  };

  //render nepali date picker handler
  const renderPicker = (fieldKey, minDate = "", disable = true) => (
    <NepaliDatePicker
      className="date"
      value={assessmentConfig?.[fieldKey] || ""}
      type="input"
      showToggle
      showNationalHoliday={false}
      calendarMode={calendarMode}
      onCalendarModeChange={(mode) => setCalendarMode(mode)}
      disabled={disable}
      onDateSelect={(date) => {
        handleChange(
          fieldKey,
          date.ad.date,
          yearLevelId,
          classroomId,
          subjectId,
        );
      }}
      minDate={minDate}
    />
  );

  //handleSelectRow
  const handleSelectRow = (e) => {
    const { checked } = e.target;

    setAssessmentSubjectConfigurations((prev) => {
      const updatedRows = prev?.map((row) => {
        if (
          row?.yearLevelId !== yearLevelId ||
          row?.classroomId !== classroomId ||
          row?.subjectId !== subjectId
        ) {
          return row;
        }

        return {
          ...row,
          checked,
        };
      });

      return updatedRows;
    });
  };

  //initialize default value
  useEffect(() => {
    if (!yearLevelId || !classroomId || !subjectId) return;

    setAssessmentSubjectConfigurations((prev) => {
      const index = prev.findIndex(
        (data) =>
          data.yearLevelId === yearLevelId &&
          data.classroomId === classroomId &&
          data.subjectId === subjectId,
      );

      const buildConfig = (existing = {}) => {
        return {
          ...existing,
          checked: existing?.isRecorded || existing.checked || false,

          ...(hasTheory && {
            hasTheory: true,
            assessmentTheoryStartTime:
              defaultStartTime || existing.assessmentTheoryStartTime || "",
            assessmentTheoryHour:
              defaultHour || existing.assessmentTheoryHour || "",
            assessmentTheoryMinutes:
              defaultMinutes || existing.assessmentTheoryMinutes || "",
          }),

          ...(hasPractical && {
            hasPractical: true,
            assessmentPracticalStartTime:
              defaultStartTime || existing.assessmentPracticalStartTime || "",
            assessmentPracticalHour:
              defaultHour || existing.assessmentPracticalHour || "",
            assessmentPracticalMinutes:
              defaultMinutes || existing.assessmentPracticalMinutes || "",
          }),
        };
      };

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = buildConfig(updated[index]);
        return updated;
      }

      return [
        ...prev,
        buildConfig({
          yearLevelId,
          classroomId,
          subjectId,
        }),
      ];
    });
  }, [
    defaultStartTime,
    defaultHour,
    defaultMinutes,
    yearLevelId,
    classroomId,
    subjectId,
    hasTheory,
    hasPractical,
  ]);

  return (
    <>
      {hasTheory && (
        <tr>
          <td rowSpan={Number(hasTheory) + Number(hasPractical)}>
            <Tooltip
              title={
                assessmentConfig?.isRecorded
                  ? "You cannot remove this subject because students marks has already been recorded."
                  : ""
              }
            >
              <input
                type="checkbox"
                checked={
                  assessmentConfig?.checked ||
                  assessmentConfig?.isRecorded ||
                  false
                }
                onChange={(e) => handleSelectRow(e)}
                className="checkbox"
                disabled={assessmentConfig?.isRecorded}
              />
            </Tooltip>
          </td>
          <td rowSpan={Number(hasTheory) + Number(hasPractical)}>
            {subjectName}
          </td>
          <td>Theory</td>
          <td>
            <div
              style={{ gap: "8px" }}
              className="create-assessessmnet-datepicker"
            >
              {renderPicker("assessmentTheoryFromDate", "", false)}
            </div>
          </td>
          <td>
            <input
              type="time"
              value={assessmentConfig?.assessmentTheoryStartTime ?? ""}
              onChange={(e) => {
                handleChange(
                  "assessmentTheoryStartTime",
                  e.target.value,
                  yearLevelId,
                  classroomId,
                  subjectId,
                );
              }}
              className="form-control"
            />
          </td>
          <td>
            <div style={{ gap: "8px" }} className="flex">
              <div style={{ gap: "8px", width: "100px" }} className="flex">
                <input
                  className="form-control"
                  type="number"
                  min={0}
                  max={23}
                  value={assessmentConfig?.assessmentTheoryHour ?? ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (
                      Number(inputValue) > 23 ||
                      Number(inputValue) < 0 ||
                      inputValue?.length > 2
                    ) {
                      swal("Oops", "Enter valid hour");
                      e.preventDefault();
                      return;
                    }

                    handleChange(
                      "assessmentTheoryHour",
                      inputValue,
                      yearLevelId,
                      classroomId,
                      subjectId,
                    );
                  }}
                  placeholder="Hour"
                />
              </div>
              <div style={{ gap: "8px", width: "100px" }} className="flex">
                <input
                  className="form-control"
                  type="number"
                  min={0}
                  max={59}
                  value={assessmentConfig?.assessmentTheoryMinutes ?? ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (
                      Number(inputValue) > 59 ||
                      Number(inputValue) < 0 ||
                      inputValue?.length > 2
                    ) {
                      swal("Oops", "Enter valid minutes");
                      e.preventDefault();
                      return;
                    }

                    handleChange(
                      "assessmentTheoryMinutes",
                      inputValue,
                      yearLevelId,
                      classroomId,
                      subjectId,
                    );
                  }}
                  placeholder="Minutes"
                />
              </div>
            </div>
          </td>
          <td>
            <div className="create-assessessmnet-datepicker">
              {renderPicker(
                "recordingTheoryDate",
                assessmentConfig?.assessmentTheoryFromDate,
                assessmentConfig?.assessmentTheoryFromDate ? false : true,
              )}
            </div>
          </td>
          <td>
            <div className="create-assessessmnet-datepicker">
              {renderPicker(
                "recordingTheoryDeadlineDate",
                assessmentConfig?.recordingTheoryDate,
                assessmentConfig?.recordingTheoryDate ? false : true,
              )}
            </div>
          </td>

          <td rowSpan={Number(hasTheory) + Number(hasPractical)}>
            <Tooltip
              title={
                assessmentConfig?.isRecorded
                  ? "You cannot remove this subject because students marks has already been recorded."
                  : ""
              }
            >
              <div>
                <Button
                  onClick={() => clearRow(yearLevelId, classroomId, subjectId)}
                  variant="danger"
                  size="sm"
                  disabled={assessmentConfig?.isRecorded}
                >
                  Clear
                </Button>
              </div>
            </Tooltip>
          </td>
        </tr>
      )}

      {hasPractical && (
        <tr>
          {!hasTheory && (
            <>
              <td rowSpan={Number(hasTheory) + Number(hasPractical)}>
                <Tooltip
                  title={
                    assessmentConfig?.isRecorded
                      ? "You cannot remove this subject because students marks has already been recorded."
                      : ""
                  }
                >
                  <input
                    type="checkbox"
                    checked={
                      assessmentConfig?.checked ||
                      assessmentConfig?.isRecorded ||
                      false
                    }
                    onChange={(e) => handleSelectRow(e)}
                    className="checkbox"
                    disabled={assessmentConfig?.isRecorded}
                  />
                </Tooltip>
              </td>
              <td rowSpan={Number(hasTheory) + Number(hasPractical)}>
                {subjectName}
              </td>
            </>
          )}
          <td>Practical</td>
          <td>
            <div
              style={{ gap: "8px" }}
              className="create-assessessmnet-datepicker flex"
            >
              {renderPicker("assessmentPracticalFromDate", "", false)}
            </div>
          </td>
          <td>
            <input
              type="time"
              value={assessmentConfig?.assessmentPracticalStartTime ?? ""}
              onChange={(e) => {
                handleChange(
                  "assessmentPracticalStartTime",
                  e.target.value,
                  yearLevelId,
                  classroomId,
                  subjectId,
                );
              }}
              className="form-control"
            />
          </td>
          <td>
            <div style={{ gap: "8px" }} className="flex">
              <div style={{ gap: "8px", width: "100px" }} className="flex">
                <input
                  className="form-control"
                  type="number"
                  min={0}
                  max={23}
                  value={assessmentConfig?.assessmentPracticalHour ?? ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (
                      Number(inputValue) > 23 ||
                      Number(inputValue) < 0 ||
                      Number(inputValue)?.length > 2
                    ) {
                      swal("Oops", "Enter valid hour");
                      e.preventDefault();
                      return;
                    }

                    handleChange(
                      "assessmentPracticalHour",
                      inputValue,
                      yearLevelId,
                      classroomId,
                      subjectId,
                    );
                  }}
                  placeholder="Hour"
                />
              </div>
              <div style={{ gap: "8px", width: "100px" }} className="flex">
                <input
                  className="form-control"
                  type="number"
                  min={0}
                  max={59}
                  value={assessmentConfig?.assessmentPracticalMinutes ?? ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (
                      Number(inputValue) > 59 ||
                      Number(inputValue) < 0 ||
                      Number(inputValue)?.length > 2
                    ) {
                      swal("Oops", "Enter valid minutes");
                      e.preventDefault();
                      return;
                    }

                    handleChange(
                      "assessmentPracticalMinutes",
                      inputValue,
                      yearLevelId,
                      classroomId,
                      subjectId,
                    );
                  }}
                  placeholder="Minutes"
                />
              </div>
            </div>
          </td>
          <td>
            <div className="create-assessessmnet-datepicker">
              {renderPicker(
                "recordingPracticalDate",
                assessmentConfig?.assessmentPracticalFromDate,
                assessmentConfig?.assessmentPracticalFromDate ? false : true,
              )}
            </div>
          </td>
          <td>
            <div className="create-assessessmnet-datepicker">
              {renderPicker(
                "recordingPracticalDeadlineDate",
                assessmentConfig?.recordingPracticalDate,
                assessmentConfig?.recordingPracticalDate ? false : true,
              )}
            </div>
          </td>

          {!hasTheory && (
            <td rowSpan={Number(hasTheory) + Number(hasPractical)}>
              <Tooltip
                title={
                  assessmentConfig?.isRecorded
                    ? "You cannot remove this subject because students marks has already been recorded."
                    : ""
                }
              >
                <div>
                  <Button
                    onClick={() =>
                      clearRow(yearLevelId, classroomId, subjectId)
                    }
                    variant="danger"
                    size="sm"
                    disabled={assessmentConfig?.isRecorded}
                  >
                    Clear
                  </Button>
                </div>
              </Tooltip>
            </td>
          )}
        </tr>
      )}
    </>
  );
};

export default AssessmentRow;
